import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useGetUnitsByAccountQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';
import getDefaultVisibleDataTypes from '../utils/defaultVisibleDataTypes';

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date).toISO();
  return encodeURIComponent(isoString);
}

function fillInGaps(data, aggregateType) {
  if (data.length === 0) return data;
  // fill in gaps based on aggregateType with null values to break line in highcharts
  // assume data is sorted by Timestamp
  // find gaps larger than 1.5x the expected interval and fill with nulls
  const filledData = [];
  const timeStepMap = {
    Raw: 5 * 60 * 1000, // assume raw is 5 min intervals
    Minutes5: 5 * 60 * 1000,
    Hour: 60 * 60 * 1000,
    Day: 24 * 60 * 60 * 1000,
  };
  const timeStep = timeStepMap[aggregateType] || 5 * 60 * 1000;
  let lastTimestamp = new Date(data[0].Timestamp).getTime();
  filledData.push(data[0]);
  for (let i = 1; i < data.length; i++) {
    const currentTimestamp = new Date(data[i].Timestamp).getTime();
    // Fill gaps
    while (currentTimestamp - lastTimestamp > timeStep * 1.5) {
      lastTimestamp += timeStep;
      // Set all fields except Timestamp to null
      const emptyPoint = Object.fromEntries(
        Object.entries(data[0]).map(([k]) => [
          k,
          k === 'Timestamp' ? new Date(lastTimestamp).toISOString() : null,
        ]),
      );
      filledData.push(emptyPoint);
    }
    filledData.push(data[i]);
    lastTimestamp = currentTimestamp;
  }
  return filledData;
}

export default function useDataViewerData({
  accountId,
  selectedUnits,
  dateRange,
  liveMode,
  aggregateOverride,
}) {
  const { data: units = [] } = useGetUnitsByAccountQuery(accountId, {
    skip: !accountId,
  });
  const [trigger] = useLazyGetDataQuery();
  const [chartSeries, setChartSeries] = useState([]);
  const [yAxes, setYAxes] = useState([]);
  const [seriesVisibility, setSeriesVisibility] = useState({});
  const chartComponentRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  // Data transformation helpers
  const recordedDataTypes = useMemo(() => {
    if (!selectedUnits || selectedUnits.length === 0) return [];
    return selectedUnits.flatMap((unit) => {
      return unit.RecordedDataTypes.map((type) => {
        return { ...type, UnitId: unit.UnitId };
      });
    });
  }, [selectedUnits]);

  const expandedRecordedDataTypes = useMemo(() => {
    const expandedList = [];
    recordedDataTypes.forEach((type) => {
      type.DataTypes.forEach((datatype) => {
        expandedList.push({
          DataType: datatype,
          UnitTypeInputId: type.UnitTypeInput.UnitTypeInputId,
          Name: type.UnitTypeInput.Name,
          UnitId: type.UnitId,
        });
      });
    });
    return expandedList;
  }, [recordedDataTypes]);

  // Default visible data types
  const availableDataTypes = useMemo(() => {
    return Array.from(new Set(chartSeries.map((d) => d.dataType)))
      .sort((a, b) => a.localeCompare(b))
      .join(',');
  }, [chartSeries]);

  const defaultVisibleDataTypes = useMemo(
    () =>
      getDefaultVisibleDataTypes(selectedUnits, availableDataTypes.split(',')),
    [selectedUnits, availableDataTypes],
  );

  // Fetch and transform chart data
  useEffect(() => {
    if (
      !expandedRecordedDataTypes ||
      expandedRecordedDataTypes.length === 0 ||
      !dateRange.from ||
      !dateRange.to
    ) {
      setChartSeries([]);
      setYAxes([]);
      return;
    }
    const toApiArgs = (unit) => ({
      from: formatAndEncodeTimestampForAPI(new Date(dateRange.from)),
      to: formatAndEncodeTimestampForAPI(new Date(dateRange.to)),
      unitId: unit.UnitId,
      datatype: unit.DataType,
      unitTypeInputId: unit.UnitTypeInputId === 0 ? null : unit.UnitTypeInputId,
      aggregateType: aggregateOverride, // let parent handle logic
    });
    let isMounted = true;
    const uniqueDataTypes = [];
    const axes = expandedRecordedDataTypes
      .filter((datatype) => {
        if (uniqueDataTypes.includes(datatype.DataType)) return false;
        uniqueDataTypes.push(datatype.DataType);
        return true;
      })
      .map((datatype, idx) => ({
        title: { text: `${datatype.DataType}` },
        opposite: idx % 2 === 1,
      }));
    const fetchAllData = async () => {
      const seriesArr = [];
      setIsLoading(true);
      await Promise.all(
        expandedRecordedDataTypes.map(async (datatype) => {
          const result = await trigger(toApiArgs(datatype));
          const data = fillInGaps(
            result?.data || [],
            aggregateOverride || 'Raw',
          );
          if (isMounted && result?.data?.length > 0) {
            const unit = units.find((u) => u.UnitId === datatype.UnitId);
            seriesArr.push({
              id: `${datatype.UnitId}-${datatype.DataType}-${datatype.UnitTypeInputId}`,
              name: `${unit?.Name || datatype.UnitId} - ${datatype.UnitTypeInputId === 0 ? 'Total' : datatype.Name} ${data[0]?.Base ? `(${data[0].Base})` : ''}`,
              type: 'line',
              data: data.map((point) => [
                DateTime.fromISO(point.Timestamp).toMillis(),
                point.Value,
              ]),
              dataType: datatype.DataType,
              base: data[0]?.Base || '',
              unit: unit,
              showInNavigator: !liveMode,
            });
          }
        }),
      );
      if (isMounted) {
        setChartSeries((prev) => {
          seriesArr.forEach((newSeries) => {
            const existingIndex = prev.findIndex((s) => s.id === newSeries.id);
            if (existingIndex !== -1) {
              newSeries.visible = chartComponentRef.current?.chart.series.find(
                (s) => s.userOptions.id === newSeries.id,
              )?.visible;
              prev[existingIndex] = newSeries;
            } else {
              newSeries.visible = getDefaultVisibleDataTypes([
                newSeries.unit,
              ]).includes(newSeries.dataType);
              prev.push(newSeries);
            }
          });
          return prev.filter((s) => seriesArr.some((ns) => ns.id === s.id));
        });
        setIsLoading(false);
        setYAxes(axes);
        setSeriesVisibility((prev) => {
          const next = { ...prev };
          for (const s of seriesArr) {
            if (!(s.id in next)) {
              next[s.id] = {
                visible: getDefaultVisibleDataTypes([s.unit]).includes(
                  s.dataType,
                ),
              };
            }
          }
          Object.keys(next).forEach((id) => {
            if (!seriesArr.some((s) => s.id === id)) delete next[id];
          });
          return next;
        });
      }
    };
    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, [
    dateRange.from,
    dateRange.to,
    liveMode,
    trigger,
    expandedRecordedDataTypes,
    aggregateOverride,
    units,
  ]);

  return {
    units,
    isLoading,
    chartSeries,
    yAxes,
    seriesVisibility,
    setSeriesVisibility,
    chartComponentRef,
    defaultVisibleDataTypes,
    expandedRecordedDataTypes,
  };
}
